import { type ReactNode, useEffect, useState } from "react";
import {
  Autocomplete, Avatar, Collapse,
  Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, Stack, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, Tabs, ToggleButton, ToggleButtonGroup,
  Typography, Button, CircularProgress, Tooltip, TextField, Alert,
  Menu, MenuItem, TableSortLabel, TablePagination, InputAdornment, Paper, Link, FormControlLabel, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import SearchIcon from "@mui/icons-material/Search";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyOverride, bulkOverride, fetchDecision, setReadState, fetchDecisions, fetchStats, fetchSplitInfo,
  setHold, splitPartUrl,
  type DecisionFilters, type Override, type Queue, type SortDir, type SortKey,
} from "@/api/decisions";
import { billPageState, createBillDraft } from "@/api/bills";
import { ThreadView } from "@/components/email/ThreadView";
import { useAuth } from "@/auth/AuthContext";
import { EmailComments } from "@/components/email/EmailComments";
import { searchVendors, fetchVendorsByIds, type VendorOption } from "@/api/referenceData";
import type {
  Action, Decision, DecisionBill, FromCategory, PageRange, SplitInfo, StatusOption,
} from "@/types";
import { ApprovalQueueTable } from "@/components/ApprovalQueueTable";
import { fetchApprovalQueueCounts, type ApprovalQueueStatus } from "@/api/approvalQueues";
import { AppLayout } from "@/components/AppLayout";
import { RemindMeButton, REMINDER_PRESETS } from "@/components/RemindMeButton";
import { BILL_REMINDERS_KEY, dismissBillReminder, fetchBillReminders, formatRemindAt, saveBillReminder } from "@/api/billReminders";

const ACTIONS: Action[] = ["quarantined", "forwarded", "split_and_forwarded", "needs_review", "none"];

// What AP sees for a stored action. The stored values predate removing the
// Charted forward and stay as-is (the accuracy reports read them), so a held
// invoice is still "forwarded". Only say Charted when mail really went there,
// i.e. the decision has forwarded_message_ids (detail view only).
const ACTION_LABEL: Record<Action, string> = {
  quarantined: "Spam",
  forwarded: "Invoice",
  split_and_forwarded: "Invoice bundle",
  needs_review: "Needs review",
  none: "Filed",
};

function actionLabel(action: Action, sentToCharted = false): string {
  if (sentToCharted && (action === "forwarded" || action === "split_and_forwarded")) return "Sent to Charted";
  return ACTION_LABEL[action] ?? action;
}

const FROM_CATEGORY_LABEL: Record<FromCategory, string> = {
  internal: "Internal",
  known_vendor: "Known vendor",
  unknown: "Unknown",
};
const FROM_CATEGORY_COLOR: Record<FromCategory, "success" | "info" | "warning"> = {
  internal: "info",
  known_vendor: "success",
  unknown: "warning",
};

// A column is sortable only when it maps to a server-side sort key. The table
// uses a fixed layout: columns with a width keep it, and the rest share what's
// left and truncate with an ellipsis, so the table always fits its container.
type Column = { label: string; align?: "right"; sort?: SortKey; width?: number };

// Laid out like an inbox: sender, then one wide column holding the labels
// (status and flags), subject and the AI summary as a snippet, then the date.
const DEFAULT_COLUMNS: Column[] = [
  { label: "From", sort: "from", width: 240 },
  { label: "Subject", sort: "subject" },
  { label: "Action", sort: "action", width: 130 },
  // Billed and the Decision log only: received → resolved.
  { label: "Time to resolve", align: "right", width: 130 },
  { label: "When", align: "right", sort: "date", width: 110 },
];

// Billing-focused view for Ready to bill: what a reviewer needs to bill.
// Vendor/PO#/Amount come from the auto-created bill draft and aren't sortable.
const FORWARDING_COLUMNS: Column[] = [
  { label: "Vendor", width: 220 },
  { label: "PO#", width: 120 },
  { label: "Amount", align: "right", width: 110 },
  { label: "Subject" },
  { label: "Received", align: "right", sort: "received", width: 140 },
  { label: "Due", sort: "due", width: 150 },
];

// When the original email arrived (a reply doesn't change it).
function receivedAt(d: Decision): string {
  return d.email.message_date ?? d.created_at;
}

// A span of time as "3d", or "5h" / "<1h" under a day.
function fmtSpan(fromIso: string, toIso: string): string {
  const hours = Math.max(0, (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 3_600_000);
  if (hours < 1) return "<1h";
  if (hours < 24) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

// Ready to bill: the received date with how long the item has been waiting.
function receivedCell(d: Decision) {
  const received = receivedAt(d);
  return (
    <Tooltip title={`Received ${new Date(received).toLocaleString()}`}>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {fmtWhen(received)} · {fmtSpan(received, new Date().toISOString())}
      </Typography>
    </Tooltip>
  );
}

// Billed / Decision log: how long from receipt to resolution ("—" while open).
function resolveCell(d: Decision) {
  if (!d.resolved_at) return "—";
  const received = receivedAt(d);
  return (
    <Tooltip title={`Received ${new Date(received).toLocaleString()} · resolved ${new Date(d.resolved_at).toLocaleString()}`}>
      <span>{fmtSpan(received, d.resolved_at)}</span>
    </Tooltip>
  );
}

// Inbox-style received date: the time for today, "Sep 1" for this year, and
// "Sep 1, 2025" for older mail. The full timestamp is on hover.
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

// Days until the bill is due, or how far past it, from the draft's due date
// (printed on the invoice or worked out from its terms). Overdue reads red.
function dueCell(bill: Decision["bill"]) {
  if (!bill?.due_date) return "—";
  const [y, m, day] = bill.due_date.split("-").map(Number);
  const due = new Date(y, m - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const plural = (n: number) => `${n} Day${n === 1 ? "" : "s"}`;
  const label = days < 0 ? `${plural(-days)} Overdue` : days === 0 ? "Due Today" : `Due In ${plural(days)}`;
  return (
    <Tooltip title={due.toLocaleDateString()}>
      <Typography variant="body2" color={days < 0 ? "error" : days <= 7 ? "warning.main" : "text.primary"} sx={{ whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Tooltip>
  );
}

// The inbox-style subject cell: labels first (the status chip, then the app's
// flags, extras folded into "+N"), then the subject, then the AI summary in
// grey as the snippet. Labels never shrink; the text truncates instead.
function SubjectCell({ d, options, snippet }: { d: Decision; options: StatusOption[]; snippet?: string | null }) {
  const [first, ...rest] = d.flags ?? [];
  const flagSx = { height: 20, fontSize: 11, flexShrink: 0 };
  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        {options.length > 0 && <StatusPicker d={d} options={options} compact />}
        {first && <Chip size="small" variant="outlined" label={first.label} sx={flagSx} />}
        {rest.length > 0 && (
          <Tooltip title={rest.map((f) => f.label).join(" · ")}>
            <Chip size="small" variant="outlined" label={`+${rest.length}`} sx={flagSx} />
          </Tooltip>
        )}
        {d.unread && (
          <Tooltip title="Unread: new since you last opened it">
            <Box component="span" aria-label="Unread" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />
          </Tooltip>
        )}
        <Tooltip title={snippet ?? ""} disableHoverListener={!snippet} placement="bottom-start">
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: d.unread ? 700 : 400 }}>{d.email.subject || "(no subject)"}</Box>
            {snippet && <Box component="span" sx={{ color: "text.secondary" }}>{`  ·  ${snippet}`}</Box>}
          </Typography>
        </Tooltip>
      </Box>
    </TableCell>
  );
}

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fmtUsd = (n: number | null | undefined) =>
  n == null || Number.isNaN(Number(n)) ? "—" : USD.format(Number(n));

// A bill draft only has a real amount once Claude has structured it. Until then
// (created/ocr_running/ocr_done/structuring) show progress rather than a
// misleading $0.00. A draft that finished but extracted nothing still shows the
// amount so a bad extraction is visible.
const BILL_DONE = ["ready_for_review", "posted"];
function billAmountCell(bill: Decision["bill"]) {
  if (!bill) return "—";
  if (bill.status === "failed") return <Typography variant="body2" color="error">Failed</Typography>;
  if (!BILL_DONE.includes(bill.status ?? "")) {
    return <Typography variant="body2" color="text.secondary">Structuring…</Typography>;
  }
  return fmtUsd(bill.amount);
}

// Triage tabs that show vendor bill approvals (live from NetSuite) instead of decisions.
const APPROVAL_QUEUES: Partial<Record<Queue, ApprovalQueueStatus>> = {
  approval_pending: "pending", approval_rejected: "rejected", approval_approved: "approved",
};

// Params omitted from the URL when they equal these defaults, to keep it tidy.
const DEFAULTS: Record<string, string | number> = {
  queue: "needs_review", sort: "date", dir: "desc", page: 1, per_page: 50,
};

function parseFilters(sp: URLSearchParams): DecisionFilters {
  return {
    queue: (sp.get("queue") as Queue) || "needs_review",
    q: sp.get("q") || undefined,
    from: sp.get("from") || undefined,
    from_category: (sp.get("from_category") as FromCategory | null) || undefined,
    vendor_id: sp.get("vendor_id") || undefined,
    action: sp.get("action") || undefined,
    date_from: sp.get("date_from") || undefined,
    date_to: sp.get("date_to") || undefined,
    hold: sp.get("hold") || undefined,
    unread: sp.get("unread") || undefined,
    sort: (sp.get("sort") as SortKey) || "date",
    dir: (sp.get("dir") as SortDir) || "desc",
    page: Number(sp.get("page")) || 1,
    per_page: Number(sp.get("per_page")) || 50,
  };
}

function activityTooltip(d: Decision): string {
  const received = d.email.message_date ?? d.created_at;
  const activity = d.email.activity_at;
  const fmt = (iso: string) => new Date(iso).toLocaleString();
  return activity && new Date(activity).getTime() > new Date(received).getTime()
    ? `Last reply ${fmt(activity)} · received ${fmt(received)}`
    : fmt(received);
}

function confidencePct(d: Decision) {
  return `${Math.round(d.confidence * 100)}%`;
}

// --- Date-range presets ------------------------------------------------------
// Presets fill the From/To date filters. Editing either date by hand no longer
// matches a preset, so the dropdown falls back to "Custom".
type DatePreset =
  | "all" | "today" | "yesterday" | "last_7" | "this_week"
  | "last_week" | "this_month" | "last_month" | "last_30" | "custom";

const DATE_PRESETS: { key: Exclude<DatePreset, "custom">; label: string }[] = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7", label: "Last 7 days" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
];

// Local-time YYYY-MM-DD (not toISOString, which is UTC and can be off by a day).
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
// Monday-start week (ISO): getDay() is 0=Sun..6=Sat → shift so Monday is 0.
function startOfWeek(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7));
}

// {} for "custom" (not derivable); both-undefined clears the filters ("all").
function presetRange(key: DatePreset, now: Date): { from?: string; to?: string } {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case "all": return { from: undefined, to: undefined };
    case "today": return { from: ymd(today), to: ymd(today) };
    case "yesterday": { const y = addDays(today, -1); return { from: ymd(y), to: ymd(y) }; }
    case "last_7": return { from: ymd(addDays(today, -6)), to: ymd(today) };
    case "last_30": return { from: ymd(addDays(today, -29)), to: ymd(today) };
    case "this_week": { const s = startOfWeek(today); return { from: ymd(s), to: ymd(addDays(s, 6)) }; }
    case "last_week": { const s = addDays(startOfWeek(today), -7); return { from: ymd(s), to: ymd(addDays(s, 6)) }; }
    case "this_month": {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: ymd(s), to: ymd(e) };
    }
    case "last_month": {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: ymd(s), to: ymd(e) };
    }
    default: return {};
  }
}

// Which preset (if any) the current From/To values correspond to.
function detectPreset(from: string | undefined, to: string | undefined, now: Date): DatePreset {
  if (!from && !to) return "all";
  for (const { key } of DATE_PRESETS) {
    const r = presetRange(key, now);
    if (r.from === from && r.to === to) return key;
  }
  return "custom";
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);
  const selectedId = id ? Number(id) : null;
  const now = new Date();

  const [searchText, setSearchText] = useState(filters.q ?? "");
  const [fromText, setFromText] = useState(filters.from ?? "");
  const debouncedSearch = useDebouncedValue(searchText, 300);
  const debouncedFrom = useDebouncedValue(fromText, 300);

  // Merge changes into the URL query string. Filter changes reset to page 1 and
  // use replace() so typing/toggling doesn't flood the history stack.
  const writeParams = (changes: Partial<Record<keyof DecisionFilters, string | number | undefined>>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const merged = { ...changes };
      if (resetPage) merged.page = undefined;
      for (const [k, v] of Object.entries(merged)) {
        if (v === undefined || v === "" || v === null || v === DEFAULTS[k]) next.delete(k);
        else next.set(k, String(v));
      }
      return next;
    }, { replace: true });
  };

  // Push debounced free-text into the URL; keep the inputs in sync with the URL
  // (e.g. on back/forward or Clear).
  useEffect(() => { writeParams({ q: debouncedSearch || undefined }); }, [debouncedSearch]);
  useEffect(() => { writeParams({ from: debouncedFrom || undefined }); }, [debouncedFrom]);
  useEffect(() => { setSearchText(filters.q ?? ""); }, [filters.q]);
  useEffect(() => { setFromText(filters.from ?? ""); }, [filters.from]);

  const stats = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  // The vendor bill approval tabs read NetSuite instead of the decisions list.
  const approvalStatus = APPROVAL_QUEUES[filters.queue];
  const approvalCounts = useQuery({ queryKey: ["approval-queue-counts"], queryFn: fetchApprovalQueueCounts });
  const decisions = useQuery({
    queryKey: ["decisions", filters],
    queryFn: () => fetchDecisions(filters),
    placeholderData: (prev) => prev, // keep the table visible while sorting/paging
    enabled: !approvalStatus,
  });

  const toggleSort = (key: SortKey) =>
    writeParams({ sort: key, dir: filters.sort === key ? (filters.dir === "desc" ? "asc" : "desc") : "desc" });

  const clearFilters = () => {
    setSearchText("");
    setFromText("");
    setSearchParams(filters.queue === "needs_review" ? {} : { queue: filters.queue }, { replace: true });
  };

  const openItem = (itemId: number, intent?: ItemIntent) => {
    const search = new URLSearchParams(searchParams);
    if (intent) search.set("intent", intent);
    navigate({ pathname: `/decisions/${itemId}`, search: search.toString() });
  };
  const [rowMenu, setRowMenu] = useState<{ d: Decision; x: number; y: number } | null>(null);
  const closeItem = () => navigate({ pathname: "/", search: searchParams.toString() });

  const list = decisions.data;
  const forwarding = filters.queue === "forwarding";
  // Within a queue every row shares one action, so only the Decision log shows it.
  const logView = filters.queue === "all";
  // Billed and the log hold finished items, so they show how long each took.
  const showResolve = logView || filters.queue === "forwarded";
  const columns = forwarding
    ? FORWARDING_COLUMNS
    : DEFAULT_COLUMNS.filter((c) => (logView || c.label !== "Action") && (showResolve || c.label !== "Time to resolve"));
  // This queue's statuses for the filter and the row dropdowns; none on Billed or the log.
  const noteRequired = stats.data?.hold_note_required ?? [];
  const statusOptions: StatusOption[] = Object.entries(stats.data?.hold_reasons?.[filters.queue] ?? {})
    .map(([key, label]) => ({ key, label, note_required: noteRequired.includes(key) }));

  // Multi-select bulk actions. Offered on the actionable queues only — dismissing
  // an already-billed item or a decision-log row is meaningless.
  const canBulk = ["needs_review", "spam_flagged", "forwarding"].includes(filters.queue);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkError, setBulkError] = useState<string | null>(null);
  const pageIds = list?.items.map((d) => d.id) ?? [];

  // Clear the selection whenever the visible set changes (queue switch, filter,
  // page) so an off-screen id can never be acted on.
  const selectionKey = `${filters.queue}|${filters.page}|${filters.per_page}|${list?.total ?? 0}`;
  useEffect(() => { setSelected(new Set()); setBulkError(null); }, [selectionKey]);

  const toggleOne = (rowId: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((pid) => selected.has(pid));
  const someOnPageSelected = pageIds.some((pid) => selected.has(pid));
  const toggleAllOnPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((pid) => next.delete(pid));
      else pageIds.forEach((pid) => next.add(pid));
      return next;
    });

  const readState = useMutation({
    mutationFn: (read: boolean) => setReadState([...selected], read),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      setSelected(new Set());
    },
  });

  const bulk = useMutation({
    mutationFn: (action: Override) => bulkOverride([...selected], action),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setSelected(new Set());
      setBulkError(res.failed.length > 0 ? `${res.failed.length} of ${res.succeeded.length + res.failed.length} could not be dismissed.` : null);
    },
    onError: () => setBulkError("Bulk action failed."),
  });

  return (
    <AppLayout maxWidth="xl">
      <Typography variant="h5" gutterBottom>Triage queues</Typography>

      <Tabs value={filters.queue} onChange={(_, v) => writeParams({ queue: v, hold: undefined, action: undefined })} sx={{ mb: 2 }}>
        <Tab value="needs_review" label={`Needs review (${stats.data?.needs_review ?? "…"})`} />
        <Tab value="spam_flagged" label={`Spam-flagged (${stats.data?.spam_flagged ?? "…"})`} />
        <Tab value="forwarding" label={`Ready to bill (${stats.data?.forwarding ?? "…"})`} />
        <Tab value="forwarded" label={`Billed (${stats.data?.forwarded ?? "…"})`} />
        <Tab value="approval_pending" label={`Pending Approval (${approvalCounts.data?.pending ?? "…"})`} />
        <Tab value="approval_rejected" label={`Rejected (${approvalCounts.data?.rejected ?? "…"})`} />
        <Tab value="approval_approved" label="Approved" />
        <Tab value="all" label="Decision log" />
      </Tabs>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
        <TextField
          size="small" placeholder={approvalStatus ? "Search vendor, invoice, memo…" : "Search from, subject, body…"} value={searchText}
          onChange={(e) => setSearchText(e.target.value)} sx={{ minWidth: 210 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {!approvalStatus && (
          <>
            <TextField
              size="small" placeholder="From contains…" value={fromText}
              onChange={(e) => setFromText(e.target.value)} sx={{ minWidth: 130 }}
            />
            <TextField
              select size="small" label="Sender" value={filters.from_category ?? ""} sx={{ minWidth: 120 }}
              onChange={(e) => writeParams({ from_category: e.target.value || undefined })}
            >
              <MenuItem value="">All senders</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
              <MenuItem value="known_vendor">Known vendor</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </TextField>
          </>
        )}
        <VendorFilter
          value={filters.vendor_id}
          onChange={(id) => writeParams({ vendor_id: id })}
        />
        {logView && (
          <TextField
            select size="small" label="Action" value={filters.action ?? ""} sx={{ minWidth: 120 }}
            onChange={(e) => writeParams({ action: e.target.value || undefined })}
          >
            <MenuItem value="">All</MenuItem>
            {ACTIONS.map((a) => <MenuItem key={a} value={a}>{actionLabel(a)}</MenuItem>)}
          </TextField>
        )}
        {!approvalStatus && statusOptions.length > 0 && (
          <TextField
            select size="small" label="Status" value={filters.hold ?? ""} sx={{ minWidth: 150 }}
            onChange={(e) => writeParams({ hold: e.target.value || undefined })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="none">No Status</MenuItem>
            {statusOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </TextField>
        )}
        {approvalStatus !== "pending" && (<>
        <TextField
          select size="small" label={approvalStatus ? "Bill date" : "Date range"} sx={{ minWidth: 120 }}
          value={detectPreset(filters.date_from, filters.date_to, now)}
          onChange={(e) => {
            const r = presetRange(e.target.value as DatePreset, now);
            writeParams({ date_from: r.from, date_to: r.to });
          }}
        >
          {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          {/* Only reachable state, never a user pick — shown when dates are hand-edited. */}
          <MenuItem value="custom" disabled>Custom</MenuItem>
        </TextField>
        <TextField
          type="date" size="small" label="From date" value={filters.date_from ?? ""}
          onChange={(e) => writeParams({ date_from: e.target.value || undefined })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date" size="small" label="To date" value={filters.date_to ?? ""}
          onChange={(e) => writeParams({ date_to: e.target.value || undefined })}
          InputLabelProps={{ shrink: true }}
        />
        </>)}
        {!approvalStatus && (
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={filters.unread === "1"}
              onChange={(e) => writeParams({ unread: e.target.checked ? "1" : undefined })}
            />
          )}
          label={<Typography variant="body2">Unread only</Typography>}
          sx={{ mr: 0 }}
        />
        )}
        <Button size="small" onClick={clearFilters}>Clear</Button>
      </Box>

      {approvalStatus ? (
        <ApprovalQueueTable
          key={approvalStatus} // each tab starts in its own order
          status={approvalStatus}
          filters={filters}
          onPage={(page) => writeParams({ page }, false)}
          onPerPage={(perPage) => writeParams({ per_page: perPage })}
        />
      ) : decisions.isLoading || !list ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {canBulk && (selected.size > 0 || bulkError) && (
            <Paper
              variant="outlined"
              sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, py: 1, mb: 1, bgcolor: "action.hover" }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.size} selected</Typography>
              <Button
                size="small" color="warning" variant="contained" startIcon={<DeleteOutlineIcon />}
                disabled={bulk.isPending || selected.size === 0}
                onClick={() => bulk.mutate("dismiss")}
              >
                {bulk.isPending ? "Dismissing…" : "Dismiss selected"}
              </Button>
              <Button size="small" variant="outlined" disabled={readState.isPending} onClick={() => readState.mutate(true)}>Mark read</Button>
              <Button size="small" variant="outlined" disabled={readState.isPending} onClick={() => readState.mutate(false)}>Mark unread</Button>
              <Button size="small" onClick={() => { setSelected(new Set()); setBulkError(null); }}>Clear</Button>
              {bulkError && <Alert severity="warning" sx={{ py: 0, ml: "auto" }}>{bulkError}</Alert>}
            </Paper>
          )}
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {canBulk && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={!allOnPageSelected && someOnPageSelected}
                      onChange={toggleAllOnPage}
                      inputProps={{ "aria-label": "Select all on page" }}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.label} align={col.align} sx={{ width: col.width }}>
                    {col.sort ? (
                      <TableSortLabel
                        active={filters.sort === col.sort}
                        direction={filters.sort === col.sort ? filters.dir : "asc"}
                        onClick={() => toggleSort(col.sort as SortKey)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
                {/* Decision Log only: who made the final call (reviewer or the agent). */}
                {filters.queue === "all" && <TableCell sx={{ width: 160 }}>Decided by</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.items.map((d) => (
                <TableRow
                  key={d.id} hover sx={{ cursor: "pointer" }} onClick={() => openItem(d.id)}
                  onContextMenu={(e) => { e.preventDefault(); setRowMenu({ d, x: e.clientX, y: e.clientY }); }}
                >
                  {canBulk && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected.has(d.id)}
                        onChange={() => toggleOne(d.id)}
                        inputProps={{ "aria-label": `Select decision ${d.id}` }}
                      />
                    </TableCell>
                  )}
                  {forwarding ? (
                    <>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ fontWeight: d.unread ? 700 : 400 }}>
                          {d.bill?.vendor_name || d.email.from_name || d.email.from_address}
                        </Typography>
                      </TableCell>
                      {d.classification === "multi_invoice" ? (
                        // A multi-invoice email has no single PO/amount. Say how it
                        // gets billed instead of showing "—": one PDF holding several
                        // invoices is split first; several PDFs are billed per file.
                        <TableCell colSpan={2}>
                          {d.email.pdf_count > 1 ? (
                            <Chip
                              size="small" color="info" variant="outlined" icon={<ReceiptLongIcon />}
                              label={`${d.email.pdf_count} PDFs · bill each`}
                              sx={{ maxWidth: "100%" }}
                            />
                          ) : (
                            <Chip
                              size="small" color="info" variant="outlined" icon={<CallSplitIcon />}
                              label={`Split to bill${d.invoice_count ? ` · ${d.invoice_count} invoices` : ""}`}
                              sx={{ maxWidth: "100%" }}
                            />
                          )}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell><Typography variant="body2" noWrap>{d.bill?.po_number || "—"}</Typography></TableCell>
                          <TableCell align="right">{billAmountCell(d.bill)}</TableCell>
                        </>
                      )}
                      <SubjectCell d={d} options={statusOptions} />
                      <TableCell align="right">{receivedCell(d)}</TableCell>
                      <TableCell>{dueCell(d.bill)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        {/* Sender name (address on hover) with its Internal/Unknown tag inline. */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                          <Tooltip title={d.email.from_address ?? ""}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: d.unread ? 700 : 400, minWidth: 0 }}>
                              {d.email.from_name?.trim() || d.email.from_address}
                            </Typography>
                          </Tooltip>
                          <Chip
                            size="small" variant="outlined" label={FROM_CATEGORY_LABEL[d.from_category]}
                            color={FROM_CATEGORY_COLOR[d.from_category]} sx={{ height: 18, fontSize: 11, flexShrink: 0 }}
                          />
                        </Box>
                      </TableCell>
                      <SubjectCell d={d} options={statusOptions} snippet={d.summary} />
                      {logView && (
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title={d.auto ? "Decided by the AI" : "Set by a reviewer"}>
                            <span>{actionLabel(d.action)}</span>
                          </Tooltip>
                        </TableCell>
                      )}
                      {showResolve && <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{resolveCell(d)}</TableCell>}
                      <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: d.unread ? 700 : undefined }}>
                        {/* Latest activity on the thread (a reply moves it up); the
                            original received time is in the tooltip. */}
                        <Tooltip title={activityTooltip(d)}>
                          <span>{fmtWhen(d.email.activity_at ?? d.email.message_date ?? d.created_at)}</span>
                        </Tooltip>
                      </TableCell>
                      {filters.queue === "all" && (
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{d.decided_by ?? "—"}</Typography>
                        </TableCell>
                      )}
                    </>
                  )}
                </TableRow>
              ))}
              {list.items.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + (canBulk ? 1 : 0) + (filters.queue === "all" ? 1 : 0)}><em>Nothing matches these filters.</em></TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={list.total}
            page={list.page - 1}
            rowsPerPage={list.per_page}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_, p) => writeParams({ page: p + 1 }, false)}
            onRowsPerPageChange={(e) => writeParams({ per_page: parseInt(e.target.value, 10) })}
          />
        </>
      )}

      {rowMenu && (
        <RowContextMenu
          d={rowMenu.d}
          position={{ left: rowMenu.x, top: rowMenu.y }}
          onClose={() => setRowMenu(null)}
          onOpen={(intent) => { setRowMenu(null); openItem(rowMenu.d.id, intent); }}
          onError={setBulkError}
        />
      )}
      <DecisionViewer id={selectedId} onClose={closeItem} />
    </AppLayout>
  );
}

// Right-click menu on a queue row: read state, the item's queue actions, bill,
// reply, comment and reminder, without opening the item first. Queue actions
// and read state run from here; the rest open the item with an intent.
function RowContextMenu({ d, position, onClose, onOpen, onError }: {
  d: Decision;
  position: { left: number; top: number };
  onClose: () => void;
  onOpen: (intent?: ItemIntent) => void;
  onError: (message: string | null) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [remindAnchor, setRemindAnchor] = useState<HTMLElement | null>(null);
  const reminders = useQuery({ queryKey: BILL_REMINDERS_KEY, queryFn: fetchBillReminders });
  const reminder = reminders.data?.reminders.find((r) => r.triage_decision_id === d.id);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["decisions"] });
    void qc.invalidateQueries({ queryKey: ["stats"] });
  };
  const fail = (what: string) => (e: unknown) => {
    const data = (e as { response?: { data?: { error?: string } } })?.response?.data;
    onError(`${what} failed${data?.error ? `: ${data.error}` : "."}`);
  };
  const run = (what: string, fn: () => Promise<unknown>, after?: (result: unknown) => void) => {
    onClose();
    onError(null);
    fn().then((r) => { refresh(); after?.(r); }).catch(fail(what));
  };
  const remindAt = (at: Date) => run("Setting the reminder", () => saveBillReminder(d.id, at, reminder?.note ?? undefined),
    () => void qc.invalidateQueries({ queryKey: BILL_REMINDERS_KEY }));

  const options = overrideOptions(d);
  const isBundleOrMulti = d.classification === "multi_invoice" || (d.email.pdf_count ?? 0) > 1;
  const canReply = !!user?.email_responder;

  return (
    <>
      <Menu
        open
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={position}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <MenuItem onClick={() => onOpen()}>Open</MenuItem>
        <MenuItem onClick={() => run("Updating read state", () => setReadState([d.id], !!d.unread))}>
          {d.unread ? "Mark as read" : "Mark as unread"}
        </MenuItem>
        <Divider />
        {canReply && <MenuItem onClick={() => onOpen("reply")}>Reply</MenuItem>}
        {canReply && <MenuItem onClick={() => onOpen("reply_all")}>Reply all</MenuItem>}
        {canReply && <MenuItem onClick={() => onOpen("forward")}>Forward</MenuItem>}
        <MenuItem onClick={() => onOpen("comment")}>Add internal comment</MenuItem>
        <MenuItem onClick={(e) => setRemindAnchor(e.currentTarget)}>
          {reminder ? `Reminder: ${formatRemindAt(reminder.remind_at)}` : "Remind me"}
          <Box component="span" sx={{ ml: "auto", pl: 2, color: "text.secondary" }}>›</Box>
        </MenuItem>
        {(options.length > 0 || d.bill?.draft_id || d.billable || isBundleOrMulti) && <Divider />}
        {d.bill?.draft_id ? (
          <MenuItem onClick={() => { onClose(); navigate(`/bills/${d.bill!.draft_id}`); }}>Open bill draft</MenuItem>
        ) : d.billable ? (
          <MenuItem
            onClick={() => run("Starting the bill draft", () => createBillDraft(d.id), (draft) => {
              const created = draft as Awaited<ReturnType<typeof createBillDraft>>;
              navigate(`/bills/${created.id}`, { state: billPageState(created) });
            })}
          >
            Draft bill
          </MenuItem>
        ) : isBundleOrMulti && d.action !== "quarantined" && d.review_state !== "resolved" ? (
          <MenuItem onClick={() => onOpen()}>Open to bill…</MenuItem>
        ) : null}
        {options.map((o) => (
          <MenuItem
            key={o.action}
            onClick={() => run(o.label, () => applyOverride(d.id, o.action))}
            sx={o.color === "error" ? { color: "error.main" } : undefined}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={remindAnchor}
        open={Boolean(remindAnchor)}
        onClose={() => setRemindAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {REMINDER_PRESETS.map((p) => (
          <MenuItem key={p.label} onClick={() => { setRemindAnchor(null); remindAt(p.at()); }}>{p.label}</MenuItem>
        ))}
        <MenuItem onClick={() => { setRemindAnchor(null); onOpen("remind"); }}>Pick a date and time…</MenuItem>
        {reminder && <Divider />}
        {reminder && (
          <MenuItem
            onClick={() => {
              setRemindAnchor(null);
              run("Cancelling the reminder", () => dismissBillReminder(reminder.id),
                () => void qc.invalidateQueries({ queryKey: BILL_REMINDERS_KEY }));
            }}
          >
            Cancel reminder
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

type ViewMode = "drawer" | "modal" | "fullscreen";
const VIEW_STORAGE_KEY = "ap-triage:decision-view";

function loadViewMode(): ViewMode {
  try {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === "modal" || saved === "fullscreen" || saved === "drawer") return saved;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return "modal";
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v: ViewMode | null) => v && onChange(v)}
      aria-label="Decision view mode"
    >
      <ToggleButton value="drawer" aria-label="Side drawer">
        <Tooltip title="Side drawer"><ViewSidebarIcon fontSize="small" /></Tooltip>
      </ToggleButton>
      <ToggleButton value="modal" aria-label="Centered modal">
        <Tooltip title="Centered modal"><WebAssetIcon fontSize="small" /></Tooltip>
      </ToggleButton>
      <ToggleButton value="fullscreen" aria-label="Full screen">
        <Tooltip title="Full screen"><FullscreenIcon fontSize="small" /></Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

// Intents from the queue's right-click menu, carried in the URL (?intent=) so
// the item opens straight to the composer, the comment box or the reminder.
type ItemIntent = "reply" | "reply_all" | "forward" | "comment" | "remind";

function DecisionViewer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [viewerParams, setViewerParams] = useSearchParams();
  const intent = (viewerParams.get("intent") as ItemIntent | null) ?? null;
  const clearIntent = () => setViewerParams((p) => { p.delete("intent"); return p; }, { replace: true });
  const [view, setView] = useState<ViewMode>(loadViewMode);

  const changeView = (v: ViewMode) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      /* localStorage unavailable — keep the choice for this session only */
    }
  };

  const qcRead = useQueryClient();
  const detail = useQuery({
    queryKey: ["decision", id],
    // Opening an item marks its thread read, so refresh the list's unread dots.
    queryFn: async () => {
      const d = await fetchDecision(id as number);
      void qcRead.invalidateQueries({ queryKey: ["decisions"] });
      return d;
    },
    enabled: id != null,
  });

  const mutation = useMutation({
    mutationFn: (action: Override) => applyOverride(id as number, action),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["decision", id] });
    },
  });

  const d = detail.data;

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h6">Email</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        {id != null && (
          <RemindMeButton decisionId={id} autoOpenDialog={intent === "remind"} onAutoOpened={clearIntent} />
        )}
        <ViewToggle value={view} onChange={changeView} />
        <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
      </Stack>
    </Stack>
  );

  const spinner = (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>
  );

  const onAction = (action: Override) => mutation.mutate(action);

  // A refused action (e.g. sending a file that's already billed) used to fail
  // silently; surface the server's reason above the buttons.
  const actionError = mutation.isError
    ? ((mutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "That didn't work. Try again.")
    : null;

  if (view === "drawer") {
    return (
      <Drawer
        anchor="right"
        open={id != null}
        onClose={onClose}
        PaperProps={{ sx: { width: 460, display: "flex", flexDirection: "column" } }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>{header}</Box>
        {!d ? spinner : (
          <DecisionPane
            key={d.id}
            d={d}
            view={view}
            pending={mutation.isPending}
            actionError={actionError}
            onAction={onAction}
            intent={intent}
            onIntentDone={clearIntent}
            renderShell={(paneBody, footer) => (
              <>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, pb: 2 }}>{paneBody}</Box>
                {footer && (
                  <Box sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>{footer}</Box>
                )}
              </>
            )}
          />
        )}
      </Drawer>
    );
  }

  return (
    <Dialog open={id != null} onClose={onClose} fullWidth maxWidth="md" fullScreen={view === "fullscreen"}>
      <DialogTitle component="div" sx={{ pb: 1 }}>{header}</DialogTitle>
      {!d ? (
        <DialogContent dividers>{spinner}</DialogContent>
      ) : (
        <DecisionPane
          key={d.id}
          d={d}
          view={view}
          pending={mutation.isPending}
          actionError={actionError}
          onAction={onAction}
          intent={intent}
          onIntentDone={clearIntent}
          renderShell={(paneBody, footer) => (
            <>
              <DialogContent dividers>{paneBody}</DialogContent>
              {footer && (
                <DialogActions sx={{ px: 3, py: 1.5, justifyContent: "flex-start" }}>{footer}</DialogActions>
              )}
            </>
          )}
        />
      )}
    </Dialog>
  );
}

function isPdfAttachment(a: { mime_type: string; filename: string }) {
  return a.mime_type.includes("pdf") || a.filename.toLowerCase().endsWith(".pdf");
}

// The server decides which files are invoices (an Excel/CSV/Word file counts
// when the email has no PDF); fall back to the PDF check for older payloads.
function isBillableAttachment(a: { mime_type: string; filename: string; billable?: boolean }) {
  return a.billable ?? isPdfAttachment(a);
}

// Primary bill action for a single-PDF email: reads the PDF and opens an
// editable bill draft. Nothing reaches NetSuite until the reviewer posts it from
// the draft. Idempotent server-side (a repeat click reuses the open draft); with
// no attachment id the backend uses the first PDF, which is why an email with
// several PDFs gets per-file actions instead of this button.
function CreateBillButton({ decisionId, disabled }: { decisionId: number; disabled?: boolean }) {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () => createBillDraft(decisionId),
    onSuccess: (draft) => navigate(`/bills/${draft.id}`, { state: billPageState(draft) }),
  });
  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={mutation.isPending ? <CircularProgress size={18} color="inherit" /> : <ReceiptLongIcon />}
      disabled={disabled || mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? "Starting draft…" : "Draft bill"}
    </Button>
  );
}

// The bill draft (if any) read from this whole PDF. Drafts that failed are
// ignored so the reviewer can try that file again.
function billForAttachment(bills: DecisionBill[], attachmentId: string): DecisionBill | undefined {
  return bills.find(
    (b) => b.attachment_id === attachmentId && b.page_start == null && b.status !== "failed",
  );
}

// One PDF's bill action when an email carries several PDFs, each its own
// invoice. Like the split editor's invoice cards, it shows how far that file has
// got: draft it, review its draft, or view the posted bill.
function AttachmentBillAction({
  decisionId, attachmentId, bill,
}: { decisionId: number; attachmentId: string; bill?: DecisionBill }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => createBillDraft(decisionId, attachmentId),
    onSuccess: (draft) => {
      // Keep the decision fresh so this row says "Review draft" on the way back.
      qc.invalidateQueries({ queryKey: ["decision", decisionId] });
      navigate(`/bills/${draft.id}`, { state: billPageState(draft) });
    },
  });
  return (
    <Box sx={{ width: 180, flexShrink: 0, px: 1 }}>
      <InvoiceBillAction
        compact
        bill={bill}
        creating={mutation.isPending}
        disabled={mutation.isPending}
        onCreate={() => mutation.mutate()}
        onOpen={(draftId) => navigate(`/bills/${draftId}`)}
      />
    </Box>
  );
}

// A draft already exists for this decision (auto-created when it entered the
// Ready to bill) — link straight to it instead of reading the PDF again.
// Shows the draft's progress when it hasn't finished structuring yet.
function ReviewBillButton({ draftId, status }: { draftId: number; status?: string | null }) {
  const navigate = useNavigate();
  const ready = status === "ready_for_review" || status === "posted";
  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={<ReceiptLongIcon />}
      onClick={() => navigate(`/bills/${draftId}`)}
    >
      {ready ? "Review draft" : "Open draft (reading…)"}
    </Button>
  );
}

function DecisionPane({
  d, view, pending, actionError, onAction, renderShell, intent, onIntentDone,
}: {
  d: Decision;
  view: ViewMode;
  pending: boolean;
  actionError?: string | null;
  onAction: (action: Override) => void;
  intent?: ItemIntent | null;
  onIntentDone?: () => void;
  // The shell decides where the scrollable body and the (non-scrolling) action
  // footer are placed — DialogContent + DialogActions for the modal, a flex
  // column for the drawer. Keeping the footer outside the scroll area is what
  // keeps the buttons pinned; a sticky element inside the scroll box let
  // content peek beneath it.
  renderShell: (body: ReactNode, footer: ReactNode) => ReactNode;
}) {
  // Give the email room to breathe in the larger views — the cramped 280px box
  // in the drawer is what made emails hard to read.
  const emailMaxHeight = view === "drawer" ? 280 : view === "modal" ? "45vh" : "65vh";

  // The server only sends `split` while the bundle still has invoices to deal
  // with, so this doesn't hinge on the review state: posting invoice 1 resolves
  // nothing until the whole bundle is billed.
  const isBundle = !!d.split;
  // Spam-flagged items: the reviewer must first clear the spam flag (move it to
  // Ready to bill) before any billing action is offered.
  const isSpam = d.action === "quarantined";
  const pdfAttachments = (d.attachments ?? []).filter(isBillableAttachment);
  // Several separate PDFs on one email, each its own invoice: billing happens
  // per file in the attachments list, not from a single footer button (which
  // could only ever reach the first PDF). A single-PDF bundle uses the split
  // editor instead.
  const multiPdf = !isBundle && pdfAttachments.length > 1;
  const bills = d.bills ?? [];
  const billedAttachmentIds = pdfAttachments
    .filter((a) => billForAttachment(bills, a.attachment_id)?.status === "posted")
    .map((a) => a.attachment_id);
  const invoiceTotal = d.invoice_count > 0 ? d.invoice_count : pdfAttachments.length;

  // Email body is collapsed to 5 lines by default (it's usually boilerplate) and
  // expands on click. Only offer the toggle when there's enough to clamp.
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const bodyText = d.email_body?.trim() || "(no text content — see attachments)";
  const bodyIsLong = bodyText.length > 240 || bodyText.split("\n").length > 5;

  // The sender's name, plus the address only when the name isn't already it,
  // so a nameless sender doesn't read "addr <addr>".
  const fromAddress = d.email.from_address ?? "";
  const fromName: string = d.email.from_name?.trim() || fromAddress;
  const showAddress = fromName.toLowerCase() !== fromAddress.toLowerCase();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const body = (
    <Stack spacing={2}>
      {/* Read like an email: subject, then sender / recipient / date. */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{d.email.subject || "(no subject)"}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, fontSize: 16 }}>{fromName.charAt(0).toUpperCase()}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap>
              <Box component="span" sx={{ fontWeight: 600 }}>{fromName}</Box>
              {showAddress && <Box component="span" sx={{ color: "text.secondary" }}>{` <${fromAddress}>`}</Box>}
            </Typography>
            {d.email_to && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">to {d.email_to}</Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {new Date(d.email.message_date ?? d.created_at).toLocaleString()}
          </Typography>
        </Stack>
      </Box>

      {/* Attachments come before the email body — they're the reason we're here. */}
      {d.attachments && d.attachments.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {d.attachments.length} attachment{d.attachments.length === 1 ? "" : "s"}
            {multiPdf && ` · ${billedAttachmentIds.length} of ${invoiceTotal} invoice${invoiceTotal === 1 ? "" : "s"} billed`}
          </Typography>
          {multiPdf && !isSpam && (
            <Typography variant="caption" color="text.secondary" display="block">
              Each PDF is its own bill. Draft and post them one at a time.
            </Typography>
          )}
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {d.attachments.map((a) => {
              const isPdf = isBillableAttachment(a);
              // Only a single PDF holding several invoices is a bundle; flag it
              // so the reviewer sees which file the split panel acts on. Separate
              // PDFs on a multi-invoice email are one invoice each.
              const multiInvoice = isPdf && isBundle;
              return (
                // One card per file: the open-the-PDF link fills it and the bill
                // action sits inside its right edge, so each button reads as
                // belonging to its file. The link and button stay siblings (no
                // button nested in an <a>).
                <Stack
                  key={a.attachment_id} direction="row" alignItems="center"
                  sx={{
                    border: "1px solid",
                    borderColor: multiInvoice ? "warning.main" : "divider",
                    bgcolor: multiInvoice ? "rgba(237, 108, 2, 0.08)" : undefined,
                    borderRadius: 1,
                    overflow: "hidden",
                    "&:hover": { borderColor: multiInvoice ? "warning.dark" : "primary.main" },
                  }}
                >
                  <Box
                    component="a"
                    href={`/api/v1/emails/${d.email.id}/attachments/${encodeURIComponent(a.attachment_id)}${a.converted && a.billable ? "?as=pdf" : ""}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, p: 1, flexGrow: 1, minWidth: 0,
                      alignSelf: "stretch",
                      textDecoration: "none", color: "text.primary",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {isPdf
                      ? <PictureAsPdfIcon color={multiInvoice ? "warning" : "error"} />
                      : <InsertDriveFileIcon color="action" />}
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500, minWidth: 0 }}>{a.filename}</Typography>
                        {multiInvoice && (
                          <Chip
                            size="small" color="warning" variant="outlined" label="Multi-invoice"
                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                          />
                        )}
                        {a.converted && a.billable && (
                          <Chip
                            size="small" color="info" variant="outlined" label="Converted to PDF"
                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {Math.max(1, Math.round(a.size / 1024))} KB · {a.converted && a.billable ? "open as PDF" : "open"}
                      </Typography>
                    </Box>
                    <OpenInNewIcon fontSize="small" color="action" />
                  </Box>
                  {/* Per-file bill action when several PDFs share one email (each is
                      its own bill). A single-PDF email uses the footer's Draft bill /
                      the split editor instead. Spam-held items offer no billing yet. */}
                  {multiPdf && isPdf && !isSpam && (
                    <AttachmentBillAction
                      decisionId={d.id}
                      attachmentId={a.attachment_id}
                      bill={billForAttachment(bills, a.attachment_id)}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Split panel sits with the attachment it acts on, above the email body. */}
      {d.split && (
        <Box
          sx={{
            p: 1.5, borderRadius: 1,
            border: "1px solid", borderColor: "primary.light", bgcolor: "action.hover",
          }}
        >
          <SplitPanel decision={d} view={view} />
        </Box>
      )}

      {/* The whole Gmail thread (with reply/forward); the stored first
          message is the fallback when Gmail can't be reached. */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
        <ThreadView
          emailId={d.email.id}
          intent={intent === "reply" || intent === "reply_all" || intent === "forward" ? intent : null}
          onIntentDone={onIntentDone}
          fallback={(
            <Box>
              <Box
                sx={{
                  whiteSpace: "pre-wrap", fontSize: view === "drawer" ? 13 : 14, lineHeight: 1.6,
                  ...(bodyExpanded
                    ? { maxHeight: emailMaxHeight, overflow: "auto" }
                    : { display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }),
                }}
              >
                {bodyText}
              </Box>
              {bodyIsLong && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setBodyExpanded((v) => !v)}
                  sx={{ mt: 0.5 }}
                >
                  {bodyExpanded ? "Show less" : "Show more"}
                </Link>
              )}
            </Box>
          )}
        />
      </Box>
      <EmailComments emailId={d.email.id} focus={intent === "comment"} onFocused={onIntentDone} />

      {/* How the AI read it: useful when drilling in, noise otherwise. */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1 }}>
        <Link component="button" type="button" variant="caption" onClick={() => setDetailsOpen((v) => !v)}>
          {detailsOpen ? "Hide AI details" : "Show AI details"}
        </Link>
        <Collapse in={detailsOpen} unmountOnExit>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <Field label="AI action" value={`${actionLabel(d.action, (d.forwarded_message_ids?.length ?? 0) > 0)}${d.auto ? " (auto)" : " (manual)"}`} />
            <Field label="Read as" value={`${d.classification} · ${confidencePct(d)} confidence`} />
            {d.flags && d.flags.length > 0 && <Field label="Flags" value={d.flags.map((f) => f.label).join(" · ")} />}
            <Field label="Summary" value={d.summary ?? ""} />
            {d.reasons && d.reasons.length > 0 && (
              <Field label="Reasons" value={d.reasons.join("; ")} />
            )}
            <Field label="Model" value={`${d.model_id ?? ""} · prompt ${d.prompt_version ?? ""}`} />
            {d.reviewed_by && (
              <Field label="Reviewed by" value={`${d.reviewed_by.email} → ${d.override_action}`} />
            )}
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );

  // The action footer lives outside the scroll area (see renderShell) so it
  // stays pinned to the bottom of the modal. Only render it — and its top
  // border — when there's actually something to show.
  const statusOptions = d.hold_options ?? [];
  const hasFooter =
    statusOptions.length > 0 ||
    !!actionError ||
    pdfAttachments.length > 0 ||
    !!d.bill?.draft_id ||
    !!d.netsuite_bill?.url ||
    d.review_state === "resolved" ||
    d.action === "quarantined" ||
    isAwaitingHuman(d);

  // A bundle bills per invoice in the split editor, and several PDFs bill per
  // file in the attachments list, so the footer bill button is only for a
  // single-invoice email. Spam-flagged items offer no billing until cleared out
  // of the spam queue. If a draft already exists (auto-created on entering the
  // queue), link to it rather than reading the PDF again.
  const billButton = !isBundle && !multiPdf && (d.bill?.draft_id ? (
    <ReviewBillButton draftId={d.bill.draft_id} status={d.bill.status} />
  ) : (
    pdfAttachments.length > 0 && !isSpam && <CreateBillButton decisionId={d.id} />
  ));

  // One toolbar: Status on the left; on the right the secondary actions, then
  // the primary one (bill, or the posted bill in NetSuite) at the far edge.
  const footer = hasFooter ? (
    <Stack spacing={1} sx={{ width: "100%" }}>
      {actionError && <Alert severity="error">{actionError}</Alert>}
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
        {statusOptions.length > 0 && (
          <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
            <StatusPicker key={d.id} d={d} options={statusOptions} />
          </Box>
        )}
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ ml: "auto" }}>
          <OverrideButtons decision={d} pending={pending} onAction={onAction} />
          {billButton}
          {d.netsuite_bill?.url && !multiPdf && (
            <Button
              variant="outlined"
              startIcon={<ReceiptLongIcon />}
              endIcon={<OpenInNewIcon />}
              component="a"
              href={d.netsuite_bill.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in NetSuite
              {d.netsuite_bill.netsuite_bill_id ? ` (#${d.netsuite_bill.netsuite_bill_id})` : ""}
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  ) : null;

  return <>{renderShell(body, footer)}</>;
}

// Either human queue: ambiguous "needs review" items, or invoices held in
// Ready to bill.
function isAwaitingHuman(d: Decision) {
  return d.review_state === "pending" || d.review_state === "pending_forward";
}

// Validates the invoice page ranges: in bounds, and contiguous with no gaps or
// overlaps between invoices. Non-invoice pages before the first invoice or after
// the last (e.g. a cover sheet or statement) are allowed. Mirrors the
// server-side check in PdfSplitter so the reviewer gets instant feedback.
function tilingError(ranges: PageRange[], pageCount: number): string | null {
  if (ranges.length === 0) return "Add at least one invoice.";
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  let cursor: number | null = null;
  for (const r of sorted) {
    if (!Number.isInteger(r.start) || !Number.isInteger(r.end) || r.start < 1 || r.end < r.start || r.end > pageCount) {
      return `Invalid range ${r.start}–${r.end} (document has ${pageCount} pages).`;
    }
    if (cursor !== null && r.start !== cursor) {
      return `Invoices must be contiguous with no gaps or overlaps (issue near page ${cursor}).`;
    }
    cursor = r.end + 1;
  }
  return null;
}

function initialRanges(info: SplitInfo, invoiceCount: number): PageRange[] {
  const proposed = info.proposed_ranges ?? [];
  if (proposed.length > 0) return proposed.map((r) => ({ start: r.start, end: r.end }));
  // No Claude boundaries (e.g. classified before the PDF was readable): if the
  // detected invoice count matches the page count, assume one invoice per page
  // so a multi_invoice bundle doesn't default to a single whole-PDF range.
  if (invoiceCount > 1 && invoiceCount === info.page_count) {
    return Array.from({ length: info.page_count }, (_, i) => ({ start: i + 1, end: i + 1 }));
  }
  return [{ start: 1, end: info.page_count }];
}

// Loads bundle info (page count + proposed boundaries), then renders the editor.
function SplitPanel({ decision, view }: { decision: Decision; view: ViewMode }) {
  const info = useQuery({
    queryKey: ["split", decision.id],
    queryFn: () => fetchSplitInfo(decision.id),
  });

  if (info.isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  }
  if (info.isError || !info.data) {
    return <Alert severity="error">Couldn’t load the PDF for splitting. Try again, or open the attachment and bill it by hand.</Alert>;
  }
  return <SplitEditor decision={decision} info={info.data} view={view} />;
}

// The bill draft (if any) already created for exactly this page range. Drafts
// that failed are ignored so the reviewer can try that invoice again.
function billForRange(bills: DecisionBill[], r: PageRange): DecisionBill | undefined {
  return bills.find(
    (b) => b.page_start === r.start && b.page_end === r.end && b.status !== "failed",
  );
}

function SplitEditor({ decision, info, view }: { decision: Decision; info: SplitInfo; view: ViewMode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const bills = decision.bills ?? [];
  const [ranges, setRanges] = useState<PageRange[]>(() => initialRanges(info, decision.invoice_count));
  // Side-by-side (PDF | editor) in modal/fullscreen; stacked in the narrow drawer.
  const twoCol = view !== "drawer";
  const embedHeight = twoCol ? (view === "fullscreen" ? "74vh" : "56vh") : 340;

  // Create a NetSuite bill for a single invoice: slice the bundle to that page
  // range and open its editor. Each invoice gets its own draft.
  const [creatingIdx, setCreatingIdx] = useState<number | null>(null);
  const createBill = useMutation({
    mutationFn: (r: PageRange) => createBillDraft(decision.id, info.attachment_id, r),
    onSuccess: (draft) => {
      // Keep the decision fresh so this invoice's card shows "Review bill draft"
      // when the reviewer comes back for the next invoice.
      qc.invalidateQueries({ queryKey: ["decision", decision.id] });
      navigate(`/bills/${draft.id}`, { state: billPageState(draft) });
    },
    onSettled: () => setCreatingIdx(null),
  });
  const rangeValid = (r: PageRange) => r.start >= 1 && r.end >= r.start && r.end <= info.page_count;
  const postedCount = bills.filter((b) => b.status === "posted").length;

  // ?as=pdf shows an Excel/CSV/Word bundle as the PDF it is split from; a real
  // PDF ignores it.
  const embedUrl = `/api/v1/emails/${decision.email.id}/attachments/${encodeURIComponent(info.attachment_id)}?as=pdf`;
  const error = tilingError(ranges, info.page_count);
  const serverError = createBill.isError
    ? (createBill.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? null
    : null;

  const update = (i: number, key: keyof PageRange, value: number) =>
    setRanges((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const removeRow = (i: number) => setRanges((rs) => rs.filter((_, idx) => idx !== i));

  // Add a new invoice starting after the last range's end (clamped to the PDF).
  const addRow = () =>
    setRanges((rs) => {
      const nextStart = Math.min((rs[rs.length - 1]?.end ?? 0) + 1, info.page_count);
      return [...rs, { start: nextStart, end: info.page_count }];
    });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} useFlexGap flexWrap="wrap">
        <Typography variant="subtitle2">
          Split bundle — {info.filename} ({info.page_count} pages)
        </Typography>
        {/* Billing a bundle is a several-sittings job, so say where it stands. */}
        {postedCount > 0 && (
          <Chip
            size="small"
            color={postedCount >= ranges.length ? "success" : "info"}
            variant="outlined"
            label={`${postedCount} of ${ranges.length} invoices billed`}
          />
        )}
      </Stack>
      <Alert severity="info" icon={false} sx={{ mb: 1, py: 0.5 }}>
        This PDF holds several invoices in one file. On the right, each invoice is a <strong>card</strong> —
        set its page range (<em>From</em>–<em>To</em>), <strong>↗</strong> preview it, then
        <strong> Draft bill</strong> to read those pages into a draft you check and post to NetSuite. Bill
        them one at a time and come back for the next: this bundle stays in the queue, and each card
        remembers its bill, until every invoice is done. The ranges must cover every page once, with no gaps or overlaps.
        {info.proposed_ranges.length > 0 && " Boundaries were proposed by Claude — adjust if they're off."}
      </Alert>

      <Box sx={{ display: "flex", flexDirection: twoCol ? "row" : "column", gap: 2, mt: 1 }}>
        <Box
          component="embed"
          src={embedUrl}
          type="application/pdf"
          sx={{
            flexGrow: twoCol ? 1 : 0, minWidth: 0, width: twoCol ? "auto" : "100%",
            height: embedHeight, border: "1px solid", borderColor: "divider", borderRadius: 1,
          }}
        />

        <Box sx={{ width: twoCol ? 340 : "100%", flexShrink: 0, display: "flex", flexDirection: "column" }}>
          <Stack spacing={1.25} sx={{ overflowY: "auto", overflowX: "hidden", maxHeight: twoCol ? "calc(74vh - 96px)" : "none", pt: 1, px: 0.25 }}>
            {ranges.map((r, i) => {
              const bill = billForRange(bills, r);
              // A posted invoice's pages are settled — re-pointing the range
              // would just orphan the bill that was made from it.
              const billed = bill?.status === "posted";
              return (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Invoice {i + 1}</Typography>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <Tooltip title="Preview this invoice">
                      <IconButton size="small" aria-label={`Preview invoice ${i + 1}`}
                        onClick={() => window.open(splitPartUrl(decision.id, r), "_blank", "noopener")}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove this invoice">
                      <span>
                        <IconButton size="small" aria-label={`Remove invoice ${i + 1}`}
                          onClick={() => removeRow(i)} disabled={ranges.length === 1 || billed}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                  <TextField
                    label="From" type="number" size="small" value={r.start} disabled={billed}
                    inputProps={{ min: 1, max: info.page_count }} sx={{ width: 88 }}
                    onChange={(e) => update(i, "start", Number(e.target.value))}
                  />
                  <Typography color="text.secondary">–</Typography>
                  <TextField
                    label="To" type="number" size="small" value={r.end} disabled={billed}
                    inputProps={{ min: 1, max: info.page_count }} sx={{ width: 88 }}
                    onChange={(e) => update(i, "end", Number(e.target.value))}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                    of {info.page_count} pages
                  </Typography>
                </Stack>

                <InvoiceBillAction
                  bill={bill}
                  creating={creatingIdx === i}
                  disabled={createBill.isPending || !rangeValid(r)}
                  onCreate={() => { setCreatingIdx(i); createBill.mutate(r); }}
                  onOpen={(draftId) => navigate(`/bills/${draftId}`)}
                />
              </Paper>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add invoice</Button>
            <Button size="small" onClick={() => setRanges(initialRanges(info, decision.invoice_count))}>Reset</Button>
          </Stack>

          {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
          {serverError && <Alert severity="error" sx={{ mt: 1 }}>{serverError}</Alert>}
        </Box>
      </Box>
    </Box>
  );
}

// One invoice's bill action, which depends on how far that invoice has got:
// no draft yet -> draft it; a draft in flight or under review -> open it;
// posted -> show the NetSuite bill. Used by the split editor's invoice cards and,
// `compact`, by each PDF row of an email with several PDFs.
function InvoiceBillAction({
  bill, creating, disabled, onCreate, onOpen, compact = false,
}: {
  bill?: DecisionBill;
  creating: boolean;
  disabled: boolean;
  onCreate: () => void;
  onOpen: (draftId: number) => void;
  compact?: boolean;
}) {
  if (bill?.status === "posted") {
    const billNo = bill.netsuite_bill_id ? ` #${bill.netsuite_bill_id}` : "";
    const link = {
      disabled: !bill.url,
      component: bill.url ? "a" : "button",
      href: bill.url ?? undefined,
      target: "_blank",
      rel: "noopener noreferrer",
    } as const;
    if (compact) {
      return (
        <Button
          fullWidth size="small" variant="outlined" color="success" sx={{ textTransform: "none" }}
          endIcon={<OpenInNewIcon fontSize="small" />}
          {...link}
        >
          Billed{billNo}
        </Button>
      );
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" color="success" variant="outlined" label="Billed" />
        <Button
          size="small" variant="outlined" sx={{ textTransform: "none", flexGrow: 1 }}
          endIcon={<OpenInNewIcon />}
          {...link}
        >
          View bill in NetSuite{billNo}
        </Button>
      </Stack>
    );
  }

  if (bill) {
    const ready = bill.status === "ready_for_review";
    return (
      <Button
        fullWidth variant="contained" color="primary" size="small" sx={{ textTransform: "none" }}
        startIcon={<ReceiptLongIcon />}
        onClick={() => onOpen(bill.draft_id)}
      >
        {ready ? "Review draft" : "Open draft (reading…)"}
      </Button>
    );
  }

  return (
    <Button
      fullWidth variant={compact ? "outlined" : "contained"} size="small" sx={{ textTransform: "none" }}
      startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <ReceiptLongIcon />}
      disabled={disabled}
      onClick={onCreate}
    >
      {creating ? "Starting draft…" : "Draft bill"}
    </Button>
  );
}

// The non-billing ways out of a queue. Billing itself happens in the bill
// buttons above (or the split editor); nothing here sends mail.
// The queue actions an item offers in its current state. The open item's
// action bar and the list's right-click menu both read this, so they can't
// drift apart.
type OverrideOption = {
  action: Override;
  label: string;
  color: "inherit" | "error" | "secondary";
  variant?: "contained";
  tooltip?: string;
};

function overrideOptions(d: Decision): OverrideOption[] {
  const dismiss: OverrideOption = {
    action: "dismiss", label: "Dismiss", color: "inherit", tooltip: "Nothing to bill here: close it without a bill",
  };
  const flagSpam: OverrideOption = { action: "confirm_spam", label: "Flag as Spam", color: "error" };
  if (d.review_state === "resolved") return [];
  if (d.action === "quarantined") {
    return [
      { action: "confirm_spam", label: "Confirm Spam", color: "inherit" },
      {
        action: "to_forwarding", label: "Not Spam: Move to Ready to Bill", color: "secondary", variant: "contained",
        tooltip: "Not spam: move it to Ready to bill, where you can draft the bill",
      },
    ];
  }
  if (d.review_state === "pending_forward") {
    return [dismiss, { action: "to_review", label: "Back to Needs Review", color: "inherit" }, flagSpam];
  }
  if (d.review_state === "pending") return [dismiss, flagSpam];
  return [];
}

function OverrideButtons({
  decision, pending, onAction,
}: {
  decision: Decision;
  pending: boolean;
  onAction: (action: Override) => void;
}) {
  if (decision.review_state === "resolved") {
    return <Typography variant="body2" color="text.secondary">Resolved.</Typography>;
  }
  const options = overrideOptions(decision);
  if (options.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      {options.map((o) => {
        const button = (
          <Button
            key={o.action} color={o.color} variant={o.variant} disabled={pending} onClick={() => onAction(o.action)}
          >
            {o.label}
          </Button>
        );
        return o.tooltip ? <Tooltip key={o.action} title={o.tooltip}><span>{button}</span></Tooltip> : button;
      })}
    </Stack>
  );
}

// Compact searchable vendor filter for the queue toolbar. The URL's vendor_id
// is the source of truth; we resolve its label by id so a shared/bookmarked
// filter shows the vendor name without a search, and query options on demand
// (there are far too many vendors to ship whole).
function VendorFilter({ value, onChange }: {
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const [input, setInput] = useState("");
  const debounced = useDebouncedValue(input, 250);

  const { data: options = [] } = useQuery({
    queryKey: ["vendor_search", debounced],
    queryFn: () => searchVendors(debounced),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const { data: resolved = [] } = useQuery({
    queryKey: ["vendor_by_id", value],
    queryFn: () => fetchVendorsByIds([value!]),
    enabled: !!value,
    staleTime: 60_000,
  });

  const selected = value ? (resolved.find((v) => String(v.id) === value) ?? null) : null;
  // Keep the selected option in the list even before the user searches, so the
  // Autocomplete can render its label.
  const merged: VendorOption[] =
    selected && !options.some((o) => String(o.id) === value) ? [selected, ...options] : options;

  return (
    <Autocomplete<VendorOption>
      size="small"
      sx={{ minWidth: 180 }}
      options={merged}
      value={selected}
      getOptionLabel={(o) => o.name ?? ""}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      onChange={(_, opt) => onChange(opt ? String(opt.id) : undefined)}
      onInputChange={(_, v, reason) => reason === "input" && setInput(v)}
      noOptionsText={debounced.trim().length < 2 ? "Type to search…" : "No vendors"}
      renderInput={(params) => <TextField {...params} label="Vendor" placeholder="Any vendor" />}
    />
  );
}

// Set why a reviewer parked an item. A status that needs a note opens a note
// field (in a small dialog when compact, i.e. in a table row) and can't be
// saved without one; any other status saves the moment it's picked. Row clicks
// inside it are stopped so picking a status doesn't open the item.
function StatusPicker({ d, options, compact = false }: { d: Decision; options: StatusOption[]; compact?: boolean }) {
  const qc = useQueryClient();
  const current = d.hold?.reason ?? "";
  const [pending, setPending] = useState<StatusOption | null>(null);
  const [note, setNote] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const mutation = useMutation({
    mutationFn: ({ reason, note }: { reason: string; note: string }) => setHold(d.id, reason, note),
    onSuccess: () => {
      setPending(null);
      setNote("");
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["decision", d.id] });
    },
  });

  const pick = (key: string) => {
    const opt = options.find((o) => o.key === key);
    if (opt?.note_required) {
      setPending(opt);
      setNote(key === current ? d.hold?.note ?? "" : "");
    } else {
      setPending(null);
      mutation.mutate({ reason: key, note: "" });
    }
  };
  const cancel = () => { setPending(null); setNote(""); };
  const save = () => pending && mutation.mutate({ reason: pending.key, note });
  const noteValid = note.trim().length > 0;

  const select = (
    <TextField
      select size="small" value={pending?.key ?? current} sx={{ minWidth: 220 }}
      onChange={(e) => pick(e.target.value)} disabled={mutation.isPending}
      label="Status" InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Status" }}
    >
      <MenuItem value=""><em>No Status</em></MenuItem>
      {options.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
    </TextField>
  );
  const noteField = (
    <TextField
      size="small" required autoFocus fullWidth={compact} placeholder={`Note for ${pending?.label ?? "this status"}`}
      value={note} onChange={(e) => setNote(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && noteValid) save(); }}
    />
  );
  const error = mutation.isError && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Could not save the status.</Alert>;

  // In a row the status is a label chip (like a Gmail label) that sizes to its
  // text, so it's always readable, and opens a menu of the queue's statuses.
  if (compact) {
    const chosen = options.find((o) => o.key === current);
    const choose = (key: string) => { setMenuAnchor(null); pick(key); };
    // The menu and note dialog render in portals, but React still bubbles their
    // clicks through this tree to the table row; stop them here.
    return (
      <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: "inline-flex", flexShrink: 0 }}>
        <Tooltip title={d.hold?.note ?? ""} disableHoverListener={!d.hold?.note}>
          <Chip
            size="small"
            clickable
            disabled={mutation.isPending}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            label={chosen ? chosen.label : "+ Status"}
            color={chosen ? "warning" : "default"}
            variant={chosen ? "filled" : "outlined"}
            aria-label="Status"
            sx={{
              height: 22, fontSize: 12, flexShrink: 0,
              ...(chosen ? {} : { borderStyle: "dashed", color: "text.secondary" }),
            }}
          />
        </Tooltip>
        <Menu anchorEl={menuAnchor} open={menuAnchor != null} onClose={() => setMenuAnchor(null)}>
          <MenuItem selected={!current} onClick={() => choose("")}><em>No Status</em></MenuItem>
          {options.map((o) => (
            <MenuItem key={o.key} selected={o.key === current} onClick={() => choose(o.key)}>
              {o.label}
              {o.note_required && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>note</Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
        <Dialog open={pending != null} onClose={cancel} fullWidth maxWidth="xs">
          <DialogTitle>{pending?.label}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>This status needs a note.</Typography>
            {noteField}
            {error}
          </DialogContent>
          <DialogActions>
            <Button onClick={cancel}>Cancel</Button>
            <Button variant="contained" disabled={!noteValid || mutation.isPending} onClick={save}>Save</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        {select}
        {pending && (
          <>
            <Box sx={{ flex: 1 }}>{noteField}</Box>
            <Button size="small" variant="contained" disabled={!noteValid || mutation.isPending} onClick={save}>Save</Button>
            <Button size="small" onClick={cancel}>Cancel</Button>
          </>
        )}
        {!pending && d.hold?.note && (
          <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>{d.hold.note}</Typography>
        )}
      </Stack>
      {error}
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
